/**
 * CardSharePage — 카드 공유 이미지 (PKA 화면정의서 SC-020 · 기능명세 FN-030).
 *
 * 카드 1장을 공유용 이미지로 합성해 보여주는 화면. 진입은 카드 상세의 '공유'.
 *
 * ── 2026-08-25 전면 개편 (시안 'SC 스캔 · 380') ──────────────────────
 * 전에는 흰 상자 안에 카드·스티커·정보 캡슐·스토어 배지를 쌓아 올렸다. 결과물이
 * 웹 화면처럼 보였고, 시세 스티커가 카드 아트워크를 가렸다.
 *
 * 지금은 **화면 전체가 곧 공유될 한 장**이다:
 *   위    카드명 (크게) · 세트 · 에디션 · 번호 · 대표시세
 *   가운데 카드 그림 하나 — 가리는 것 없이
 *   아래  PEEKA 워드마크 (번개는 카드 오른쪽 위 모서리에 걸친다)
 *   하단  공유하기 (**고정 영역** — 결과물에는 안 들어간다)
 *
 * 배경은 네 겹이다 (CSS 주석 참조) — 바탕 · 카드 색 번짐 · 픽셀 도트 · 이펙트.
 * 카드는 탭하면 뒤집힌다 (CardFlip · 2026-08-25). 실물 카드는 손에 쥐면
 * 뒤집어 보는 물건이고, 이 화면은 그 카드가 주인공이라 탭에 다른 뜻이 없다.
 *
 * ⚠ 화면과 **나가는 그림은 서로 다르다** (2026-08-27). 화면은 기기 비율대로
 *   시원하게 보이고, 공유되는 이미지는 1080×1350(4:5) 로 따로 굽는다
 *   (components/card/shareImage.ts).
 *
 * ⚠ design 화면을 **실 API(useCardDetailQuery)로 이식한 것**이다 (2026-08-28).
 *   design 원본은 더미 카탈로그(findCard·getEditionQuotes·colorKey)를 읽는데,
 *   dev 는 이 화면이 이미 실 카탈로그로 넘어가 있다. 바뀐 것은 자료 출처뿐이고
 *   구성·문구·배경 네 겹은 design 그대로다:
 *     colorKey  더미 전용 색 키라 넘기지 않는다 → CardFlip·ShareBackdrop·
 *               shareImage 가 중립 팔레트로 떨어진다 (셋 다 옵셔널로 열어 둠).
 *     ticker    실 카탈로그에 티커가 없다 → 같은 자리에 **세트명**을 쓴다.
 *     시세      상세와 같은 규칙 — §5.4 없으면 "시세 없음", §4.2 비회원 마스킹.
 */
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AspectRatio } from "@astryxdesign/core/AspectRatio";
import { Button } from "@astryxdesign/core/Button";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { Heading } from "@astryxdesign/core/Heading";
import { Section } from "@astryxdesign/core/Section";
import { Skeleton } from "@astryxdesign/core/Skeleton";
import { Text } from "@astryxdesign/core/Text";
import { VStack, HStack } from "@astryxdesign/core/Layout";
import { useMoney } from "../../hooks/useMoney";
import { cardImageUrl } from "../../api/card";
import { useCardDetailQuery } from "../../hooks/useCardQueries";
import { useAuthStore } from "../../store/authStore";
import { maskPrice } from "../../lib/priceMasking";
import { useToast } from "../../components/feedback/Toast";
import { buildAppLinkUrl, share } from "../../native/share";
import CardFlip from "../../components/card/CardFlip";
import { renderShareImage } from "../../components/card/shareImage";
import ShareBackdrop from "../../components/card/ShareBackdrop";
import { track } from "../../lib/tracking";
import styles from "./CardSharePage.module.css";

/**
 * @param cardSerialNo ActivityHost 전용 — activity 안에서는 라우트 매치가 없어 useParams
 *   가 비므로 resolver 가 URL 에서 파싱해 넘긴다. 라우트 렌더에서는 생략.
 */
export default function CardSharePage({
  cardSerialNo: serialNoProp,
}: { cardSerialNo?: number } = {}) {
  const { t } = useTranslation("card");
  const money = useMoney();
  const navigate = useNavigate();
  const { cardSerialNo: paramSerialNo } = useParams<{ cardSerialNo: string }>();
  const rawSerialNo =
    serialNoProp !== undefined ? String(serialNoProp) : paramSerialNo;
  const isAuthenticated = useAuthStore((s) => !!s.accessToken);

  const toast = useToast();

  /** 공유 시트가 열려 있는 동안 두 번 눌리지 않게 */
  const [isSharing, setIsSharing] = useState(false);

  const cardSerialNo = rawSerialNo && /^\d+$/.test(rawSerialNo) ? Number(rawSerialNo) : null;
  const detail = useCardDetailQuery(cardSerialNo);
  const card = detail.data;

  if (detail.isPending && cardSerialNo !== null) {
    return (
      <Section variant="transparent" padding={5} maxWidth={720} width="100%" minHeight="100%">
        <Skeleton height={420} />
      </Section>
    );
  }

  if (!card) {
    return (
      <Section variant="transparent" padding={4} maxWidth={720} width="100%" minHeight="100%">
        <EmptyState
          title={t("detail.notFound")}
          description={t("detail.notFoundBody")}
          actions={
            <Button
              variant="primary"
              label={t("detail.backToSearch")}
              clickAction={() => navigate("/search")}
            />
          }
        />
      </Section>
    );
  }

  const listPrice = card.listPrice;
  const hasPrice = listPrice.display !== "NONE" && listPrice.amount !== null;
  /** 화면·공유문구가 같은 값을 쓴다 — §4.2 비회원은 마스킹된 값 그대로 나간다. */
  const priceLabel = !hasPrice
    ? t("price.noPrice")
    : isAuthenticated
      ? money.format(listPrice.amount ?? 0, listPrice.currency ?? undefined)
      : maskPrice(listPrice.amount ?? 0);
  const imageUrl = cardImageUrl(card.largeImagePathway ?? card.imagePathway);
  const metaLine = t("share.meta", {
    ticker: card.cardset.name,
    edition: t(`edition.${card.language}`),
    no: card.number ?? "-",
  });

  /**
   * '공유하기' — OS 공유 시트에 **이미지 + 카드명·시세·링크**를 넘긴다.
   *
   * 세 단으로 떨어진다. 위에서 되면 아래는 안 간다:
   *   ① 이미지 첨부 — 4:5 PNG 를 구워 파일로 넘긴다 (canShare 로 확인)
   *   ② 글·링크만  — 이미지가 안 되는 환경(파일 공유 미지원 WebView 등)
   *   ③ 클립보드   — 공유 시트 자체가 없는 데스크탑 브라우저
   *
   * ⚠ 사용자가 시트를 **닫은 것**(AbortError)과 시트가 **없는 것**은 다르다.
   *   닫았을 때 다음 단으로 넘어가면 닫자마자 시트가 또 열린다.
   * ⚠ 네이티브 Android WebView 에는 navigator.share 자체가 없어 ② 로 간다.
   *   거기서도 이미지를 붙이려면 파일 쓰기(@capacitor/filesystem)가 필요한데
   *   지금 의존성에 없다 — 개발 작업(OI-12)이라 남겨 둔다.
   */
  const handleShare = async () => {
    if (isSharing) return;
    setIsSharing(true);
    try {
      const text = t("share.body", { name: card.name, price: priceLabel });
      /* 카드 단위 스마트 링크 (IA rev6) — 앱이 있으면 딥링크, 없으면 웹으로.
         **항상 붙는다** (2026-08-25, '링크 포함' 토글 제거). 링크가 빠지면
         받은 사람이 그 카드로 갈 길이 없어 공유가 스크린샷과 같아진다. */
      const url = buildAppLinkUrl(`/cards/${card.cardSerialNo}`);

      /* ① 이미지 첨부 — 굽는 데 실패하면(캔버스 불가·아트워크 CORS) 조용히
         다음 단으로 넘어간다. 공유가 막히는 것보다 그림 없이라도 나가는 게 낫다. */
      const image = await renderShareImage({
        cardName: card.name,
        meta: metaLine,
        price: priceLabel,
        imgUrl: imageUrl ?? undefined,
      });
      if (image) {
        const file = new File([image], `peeka-${card.cardSerialNo}.png`, {
          type: "image/png",
        });
        if (navigator.canShare?.({ files: [file] })) {
          try {
            /* 파일과 함께 보낼 때는 url 을 따로 넘기지 않는다 — 무시하는
               앱이 많아 링크가 통째로 사라진다. 본문에 붙여 함께 보낸다. */
            await navigator.share({
              files: [file],
              title: card.name,
              text: `${text}\n${url}`,
            });
            // 취소(AbortError)는 아래 catch 로 빠져 세지 않는다 — 공유 "시도"가 아니라
            // 실제로 나간 것만 센다.
            track("card_share", { cardSerialNo: card.cardSerialNo, channel: "image" });
            return;
          } catch (err) {
            if ((err as DOMException)?.name === "AbortError") return;
          }
        }
      }

      /* ② 글·링크만 */
      const result = await share({
        title: card.name,
        text,
        url,
        dialogTitle: t("share.action"),
      });
      if (result.shared) {
        track("card_share", { cardSerialNo: card.cardSerialNo, channel: "link" });
        return;
      }

      const fallback = `${text}\n${url}`;
      try {
        await navigator.clipboard.writeText(fallback);
        track("card_share", { cardSerialNo: card.cardSerialNo, channel: "clipboard" });
        toast.success(t("share.copied"));
      } catch {
        toast.info(t("share.unavailable"));
      }
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <div className={styles.page}>
      {/* ── 배경 네 겹 (CSS 주석 참조) ───────────────────────────── */}
      <span className={styles.bgBase} aria-hidden />
      {/* 카드 색 번짐 — 더미의 colorKey 그라디언트 자리다. 실 카탈로그에는 카드별
          색 키가 없어 브랜드 색으로 깐다 (카드마다 달라지지 않는다). */}
      <span className={styles.bgTint} aria-hidden />
      <span className={styles.bgDots} aria-hidden />
      {/* 이펙트 — Balsa UI 절차적 그라디언트(studio-background). */}
      <ShareBackdrop />

      {/* 위아래 여백만 따로 눌렀다 (paddingBlock · 2026-08-25). 좌우는 36px 그대로,
          위아래만 24px 이다 — 좌우 여백만큼 위를 비우면 글이 화면 가운데로 밀려
          내려간다.
          ⚠ 이보다 줄이지 말 것. 0 에서는 카드명이 앱바(z-index 200) 뒤로 숨는다. */}
      <Section
        variant="transparent"
        padding={6}
        paddingBlock={4}
        maxWidth={720}
        width="100%"
        minHeight="100%"
      >
        <VStack gap={8} className={styles.content}>
          {/* ── 글 — 카드가 무엇이고 얼마인가 ─────────────────────
              왼쪽 정렬이다. 가운데 정렬은 카드 그림과 축이 겹쳐 무엇이 주인공인지
              흐려진다 — 글은 왼쪽에서 시작하고 그림이 가운데 선다.
              gap 1 = 4px — 카드명·메타·시세가 **한 덩어리**로 읽혀야 하는 자리다. */}
          <VStack gap={1} align="start">
            {/* 크기는 h3(20px), 문서 위계는 h1 (2026-08-25 두 단 내림). */}
            <Heading level={3} accessibilityLevel={1} wordBreak="break-all">
              {card.name}
            </Heading>
            <Text type="supporting" color="secondary">
              {metaLine}
            </Text>
            {/* 시세 — 라벨('대표시세:')도 기준 시각도 걷었다 (2026-08-25).
                이 화면에서 큰 숫자로 서 있는 값은 이것 하나뿐이라 이름이 없어도 읽힌다. */}
            <HStack gap={1} align="end" wrap="wrap">
              <Text weight="bold" color={hasPrice ? undefined : "secondary"}>
                {priceLabel}
              </Text>
              {/* 참고 시세(REFERENCE_CASE)는 그 성격을 곁말로 밝힌다 — 상세와 같은 규칙 */}
              {listPrice.display === "REFERENCE_CASE" && (
                <Text type="supporting" color="secondary">
                  {t("price.referenceCaseNotice")}
                </Text>
              )}
            </HStack>
          </VStack>

          {/* ── 카드 + 번개 ─────────────────────────────────────
              ⚠ 카드 위에 아무것도 얹지 않는다. 전에는 시세 스티커가 아트워크를
                가렸다 — 공유하려는 것이 바로 그 그림이다. */}
          <div className={styles.cardSlot}>
            <AspectRatio ratio={63 / 88}>
              <CardFlip imgUrl={imageUrl ?? undefined} cardName={card.name} />
            </AspectRatio>
            <span className={styles.bolt} aria-hidden />
          </div>

          {/* ── 브랜딩 — 워드마크 한 줄 ────────────────────────── */}
          <span
            className={styles.wordmark}
            role="img"
            aria-label={t("share.serviceName")}
          />

          {/* ── 고정 영역 — 결과물에는 안 들어간다 ────────────────── */}
          <div className={styles.bottom}>
            <Button
              variant="primary"
              size="lg"
              label={t("share.action")}
              clickAction={handleShare}
              isDisabled={isSharing}
              width="100%"
            />
          </div>
        </VStack>
      </Section>
    </div>
  );
}
